import {isCopilot} from '@github-ui/assignees/copilot-user'
import {type HIDE_OPTIONS, HideCommentActionItems} from '@github-ui/commenting/HideCommentActions'
import {copyText} from '@github-ui/copy-to-clipboard'
import {CommandActionListItem, ScopedCommands} from '@github-ui/ui-commands'
import {
  BlockedIcon,
  EyeClosedIcon,
  EyeIcon,
  FoldIcon,
  KebabHorizontalIcon,
  LinkIcon,
  PencilIcon,
  QuoteIcon,
  TrashIcon,
  UnfoldIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {useState} from 'react'
import {
  OrganizationBlockDialog,
  OrganizationUnblockDialog,
  PersonalBlockDialog,
  PersonalUnblockDialog,
} from './ConversationItemBlockDialogs'

const DEFAULT_TOGGLE_MINIMIZE_LABELS = {expand: 'Unminimize comment', collapse: 'Minimize comment'}

interface ConversationItemActionsProps {
  accessibleLabel?: string
  authorLogin: string
  contentId: string
  contentUrl: string
  link: string
  navigationSource: string
  onEdit?: () => void
  onQuoteReply?: () => void
  onDelete?: () => void
  isMinimized?: boolean
  isHidden?: boolean
  onHide?: (reason: HIDE_OPTIONS) => void
  onUnhide?: () => void
  onToggleMinimize?: () => void
  // Labels for the fold/unfold toggle button. Defaults to the generic comment wording.
  toggleMinimizeLabels?: {expand: string; collapse: string}
  githubHelpUrl?: string
  organizationLogin?: string
  viewerCanBlockFromOrg: boolean
  viewerCanBlockPersonally?: boolean
  viewerCanUnblockFromOrg: boolean
  viewerCanUnblockPersonally?: boolean
  ref?: React.RefObject<HTMLButtonElement | null>
}

export function ConversationItemActions({
  accessibleLabel,
  authorLogin,
  contentId,
  contentUrl,
  link,
  navigationSource,
  onEdit,
  onQuoteReply,
  onDelete,
  isMinimized = false,
  isHidden = false,
  onHide,
  onUnhide,
  onToggleMinimize,
  toggleMinimizeLabels = DEFAULT_TOGGLE_MINIMIZE_LABELS,
  githubHelpUrl,
  organizationLogin,
  viewerCanBlockFromOrg,
  viewerCanBlockPersonally = false,
  viewerCanUnblockFromOrg,
  viewerCanUnblockPersonally = false,
  ref,
}: ConversationItemActionsProps) {
  const [isBlockFromOrgOpen, setIsBlockFromOrgOpen] = useState(false)
  const [isPersonalBlockOpen, setIsPersonalBlockOpen] = useState(false)
  const [isPersonalUnblockOpen, setIsPersonalUnblockOpen] = useState(false)
  const [isUnblockFromOrgOpen, setIsUnblockFromOrgOpen] = useState(false)
  const canBlockPersonally = viewerCanBlockPersonally && !isCopilot(authorLogin)
  const canBlockFromOrg = viewerCanBlockFromOrg && !!organizationLogin && !isCopilot(authorLogin)
  const canUnblockFromOrg = viewerCanUnblockFromOrg && !!organizationLogin
  const viewerCanSeeHideButton = !!onHide
  const viewerCanSeeUnhideButton = !!onUnhide

  return (
    <>
      <PersonalBlockDialog
        contentId={contentId}
        authorLogin={authorLogin}
        contentUrl={contentUrl}
        isOpen={isPersonalBlockOpen}
        navigationSource={navigationSource}
        onClose={() => setIsPersonalBlockOpen(false)}
      />
      <PersonalUnblockDialog
        authorLogin={authorLogin}
        isOpen={isPersonalUnblockOpen}
        navigationSource={navigationSource}
        onClose={() => setIsPersonalUnblockOpen(false)}
      />
      {organizationLogin && (
        <OrganizationBlockDialog
          contentId={contentId}
          authorLogin={authorLogin}
          contentUrl={contentUrl}
          isOpen={isBlockFromOrgOpen}
          navigationSource={navigationSource}
          organizationLogin={organizationLogin}
          onClose={() => setIsBlockFromOrgOpen(false)}
        />
      )}
      {organizationLogin && (
        <OrganizationUnblockDialog
          organizationLogin={organizationLogin}
          authorLogin={authorLogin}
          isOpen={isUnblockFromOrgOpen}
          navigationSource={navigationSource}
          onClose={() => setIsUnblockFromOrgOpen(false)}
        />
      )}
      {onToggleMinimize && isHidden && (
        <IconButton
          icon={isMinimized ? UnfoldIcon : FoldIcon}
          variant="invisible"
          size="small"
          aria-label={isMinimized ? toggleMinimizeLabels.expand : toggleMinimizeLabels.collapse}
          onClick={onToggleMinimize}
        />
      )}
      <ActionMenu anchorRef={ref}>
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            variant="invisible"
            size="small"
            aria-label={accessibleLabel ?? 'More actions'}
            description={accessibleLabel ? 'More actions' : undefined}
          />
        </ActionMenu.Anchor>

        <ActionMenu.Overlay align="end">
          <ActionList>
            {onQuoteReply && (
              <CommandActionListItem
                commandId="pull-requests:quote-reply"
                onSelect={onQuoteReply}
                leadingVisual={<QuoteIcon />}
              >
                Quote reply
              </CommandActionListItem>
            )}
            <ActionList.Item onSelect={() => copyText(link)}>
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            {!!(onEdit || onDelete || (onHide && !isHidden) || (onUnhide && isHidden)) && <ActionList.Divider />}
            {onEdit && (
              <ActionList.Item onSelect={onEdit}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
            )}
            {onHide && viewerCanSeeHideButton && !isHidden && (
              <ActionMenu>
                <ActionMenu.Anchor>
                  <ActionList.Item>
                    <ActionList.LeadingVisual>
                      <EyeClosedIcon />
                    </ActionList.LeadingVisual>
                    Hide for everyone
                  </ActionList.Item>
                </ActionMenu.Anchor>

                <ActionMenu.Overlay align="end">
                  <ActionList>
                    <HideCommentActionItems onSelect={onHide} githubHelpUrl={githubHelpUrl} />
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            )}
            {onUnhide && viewerCanSeeUnhideButton && isHidden && (
              <ActionList.Item onSelect={onUnhide}>
                <ActionList.LeadingVisual>
                  <EyeIcon />
                </ActionList.LeadingVisual>
                Unhide for everyone
              </ActionList.Item>
            )}
            {onDelete && (
              <ActionList.Item onSelect={onDelete} variant="danger">
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            )}
            {(canBlockPersonally || viewerCanUnblockPersonally || canBlockFromOrg || canUnblockFromOrg) && (
              <ActionList.Divider />
            )}
            {canBlockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalBlockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {viewerCanUnblockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalUnblockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
            {!canBlockPersonally && !viewerCanUnblockPersonally && canBlockFromOrg && (
              <ActionList.Item onSelect={() => setIsBlockFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {!canBlockPersonally && !viewerCanUnblockPersonally && canUnblockFromOrg && (
              <ActionList.Item onSelect={() => setIsUnblockFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

export function ConversationItemActionsProvider({
  onQuoteReply,
  children,
}: React.PropsWithChildren<Pick<ConversationItemActionsProps, 'onQuoteReply'>>) {
  return <ScopedCommands commands={{'pull-requests:quote-reply': onQuoteReply}}>{children}</ScopedCommands>
}
